import React, { useState, useEffect } from 'react';

const CertUpdateModal = ({ isOpen, onClose, changes, onApply }) => {
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [selectAll, setSelectAll] = useState(true);
  const [fieldSelections, setFieldSelections] = useState({});
  
  // Initialize field selections when changes prop changes
  useEffect(() => {
    if (changes) {
      const initialSelections = {};
      changes.forEach((change, index) => {
        if (change.hasChanged) {
          initialSelections[index] = change.selected !== false; // Default to true unless explicitly false
        }
      });
      setFieldSelections(initialSelections);
      
      // Update selectAll state based on initial selections
      const allSelected = Object.values(initialSelections).every(selected => selected);
      setSelectAll(allSelected);
    }
  }, [changes]);
  
  if (!isOpen) return null;

  const hasChanges = changes.some(change => change.hasChanged);

  const handleSelectAllChange = (checked) => {
    setSelectAll(checked);
    const newSelections = {};
    changes.forEach((change, index) => {
      if (change.hasChanged) {
        newSelections[index] = checked;
      }
    });
    setFieldSelections(newSelections);
  };

  const handleFieldSelectionChange = (index, checked) => {
    const newSelections = {
      ...fieldSelections,
      [index]: checked
    };
    setFieldSelections(newSelections);
    
    // Update selectAll state
    const changedFields = changes.filter(change => change.hasChanged);
    const allSelected = changedFields.every((_, idx) => newSelections[idx]);
    setSelectAll(allSelected);
  };

  const handleApply = () => {
    const selectedChanges = [];
    changes.forEach((change, index) => {
      if (change.hasChanged && fieldSelections[index] === true) {
        selectedChanges.push(change);
      }
    });
    onApply(selectedChanges);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
          <div>
            <div className="mt-3 text-left sm:mt-5">
              <h3 className="text-2xl font-bold text-gray-900">
                Review Updates
              </h3>
              <div className="mt-2">
                <p className="text-base text-gray-600">
                  Below is the current data from the TPG. Select which changes you want to apply.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-between items-center">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="select-all"
                checked={selectAll}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                onChange={(e) => handleSelectAllChange(e.target.checked)}
              />
              <label htmlFor="select-all" className="ml-2 text-sm text-gray-700">
                Select All Changes
              </label>
            </div>
            <button
              type="button"
              onClick={() => setShowUnchanged(!showUnchanged)}
              className="text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-md px-3 py-1.5"
            >
              {showUnchanged ? 'Hide' : 'Show'} Unchanged Fields
            </button>
          </div>

          <div className="mt-4 max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="w-12 px-3 py-2"></th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Field
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Value
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    New Value
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {changes
                  .map((change, originalIndex) => ({ ...change, originalIndex }))
                  .filter(change => showUnchanged || change.hasChanged)
                  .map((change, displayIndex) => (
                    <tr key={change.originalIndex} className={!change.hasChanged ? 'bg-gray-50' : undefined}>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {change.hasChanged && (
                          <input
                            type="checkbox"
                            id={`change-${change.originalIndex}`}
                            checked={fieldSelections[change.originalIndex] || false}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            onChange={(e) => handleFieldSelectionChange(change.originalIndex, e.target.checked)}
                          />
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {change.field === 'current_population' ? 'Current Population' :
                         change.field === 'current_pop_higher' ? 'Current Pop Higher' :
                         change.field.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {change.oldValue || '(empty)'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-green-600">
                        {change.newValue || '(empty)'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="mt-5 sm:mt-6 flex justify-end space-x-3">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={handleApply}
            >
              Apply Selected Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertUpdateModal;
